'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { dispatch, useSelector } from '@/lib/store'
import { updateCommonReducerData } from '@/lib/reducers/commonReducer'
import { updateLoginData } from '@/lib/reducers/loginReducer'
import {
  BLOG_PAGE,
  CURRENT_SUBSIDIARY,
  FORM_TYPE_LAB_TEST,
  MY_REPORTS_PAGE,
  NEARBY_LABS_PAGE,
} from '@/utils/constants'
import { loadState } from '@/utils/localStorage'
import usePopularHealthCheckupsAndSearchProducts from '@/hooks/usePopularHealthCheckupsAndSearchProducts'
import {
  getCarePackageCategoryUrl,
  getPopularHealthCheckupUrl,
  getDiseaseCategoriesUrl,
} from '@/utils/commonFunc'
import { eventConstants, trackEvent } from '@/utils/events'
import LOGO_HELPER from '@/helper/logoHelper'
import {
  getDiseaseCategoriesProductListUrl,
  getProductUrl,
} from '@/utils/commonFunc'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Accordion } from '@/components/ui/accordion'
import { X } from 'lucide-react'
import MobileNavAccordion from './MobileNavAccordion'

const loggedInMobileNumber = loadState('ctxVal') || null

interface MobileDrawerProps {
  open: boolean
  onOpenChange: () => void
}

const MobileDrawer = ({ open, onOpenChange }: MobileDrawerProps) => {
  const router = useRouter()
  const { handleSubsidiaryLogo } = LOGO_HELPER()

  // This drawer is the mobile counterpart of SecondaryNav and shows the same submenus; keyed to `open` so visitors who never open it pay nothing.
  usePopularHealthCheckupsAndSearchProducts({ loadPopular: open })

  const packageState = useSelector((state) => state.package) || {}
  const commonState = useSelector((state) => state.common) || {}
  const loginState = useSelector((state) => state.login) || {}
  const cityState = useSelector((state) => state.city) || {}

  const myReportClickHandler = (e: any) => {
    e.preventDefault()
    if (!loginState?.isUserLoggedIn) {
      dispatch(updateLoginData({ ...loginState, loginModalIsOpen: true }))
      return
    } else {
      trackEvent(eventConstants.MY_REPORTS, {
        u_mb: loggedInMobileNumber,
        subsidiary: CURRENT_SUBSIDIARY,
      })
      router.push(MY_REPORTS_PAGE)
    }
  }

  return (
    <Drawer
      direction="left"
      open={open}
      onClose={onOpenChange}
      onOpenChange={onOpenChange}
    >
      <DrawerContent className="mt-0 rounded-none sm:rounded-md md:rounded-xl lg:rounded-xl xl:rounded-xl inset-0 w-[90vw] handle_remove">
        <DrawerHeader className="px-5 py-4 flex items-center justify-between">
          <DrawerTitle className="text-body-lg md:text-heading-md font-semibold">
            {handleSubsidiaryLogo(cityState?.currentCityObj?.subsidiary)}
          </DrawerTitle>
          <DrawerClose>
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
              <X size={18} className="text-gray-800" />
            </div>
          </DrawerClose>
        </DrawerHeader>

        <div className="overflow-y-auto">
          <Accordion type="single" collapsible className="w-full">
            {/* Popular Test/Packages */}
            <MobileNavAccordion
              value="popular"
              triggerLabel="Popular Test/Packages"
              sections={[
                {
                  title: 'Popular Tests',
                  items: packageState?.mostPopularTest || [],
                  getItemId: (item) => item?.productDto?.id,
                  getItemName: (item) => item?.productDto?.name,
                  getItemUrl: (item) =>
                    getProductUrl(
                      item?.productDto,
                      cityState?.currentCityObj?.slugName
                    ),
                  limit: 5,
                },
                {
                  title: 'Popular Packages',
                  items: packageState?.mostPopularPackage || [],
                  getItemId: (item) => item?.productDto?.id,
                  getItemName: (item) => item?.productDto?.name,
                  getItemUrl: (item) =>
                    getProductUrl(
                      item?.productDto,
                      cityState?.currentCityObj?.slugName
                    ),
                  limit: 5,
                },
              ]}
              viewAllUrl={getPopularHealthCheckupUrl(
                cityState?.currentCityObj?.slugName
              )}
              viewAllText="View All →"
              onLinkClick={onOpenChange}
            />

            {/* Full Body Checkup */}
            <MobileNavAccordion
              value="fullbody"
              triggerLabel="Full Body Checkup"
              sections={[
                {
                  items: packageState?.carePackageList || [],
                  getItemId: (item) => item?.packageId,
                  getItemName: (item) => item?.name,
                  getItemUrl: (item) =>
                    getCarePackageCategoryUrl(
                      cityState?.currentCityObj?.slugName,
                      item?.name
                    ),
                  limit: 8,
                },
              ]}
              onLinkClick={onOpenChange}
            />

            {/* Disease Categories */}
            <MobileNavAccordion
              value="disease"
              triggerLabel="Disease Categories"
              sections={[
                {
                  items: packageState?.diseaseCategoryDtoList || [],
                  getItemId: (item) => item?.diseaseCategory,
                  getItemName: (item) => item?.diseaseCategoryName,
                  getItemUrl: (item) =>
                    getDiseaseCategoriesProductListUrl(
                      cityState?.currentCityObj?.slugName,
                      item?.diseaseCategoryName
                    ),
                  limit: 8,
                },
              ]}
              viewAllUrl={getDiseaseCategoriesUrl(
                cityState?.currentCityObj?.slugName
              )}
              viewAllText="View All Categories →"
              onLinkClick={onOpenChange}
            />
          </Accordion>

          {/* Simple menu items */}
          <ul>
            <li className="py-3 px-5 border-t border-gray-100">
              <button
                className="text-label-sm leading-6 text-gray-800 w-full text-left"
                onClick={(e) => {
                  myReportClickHandler(e)
                  onOpenChange()
                }}
              >
                My Reports
              </button>
            </li>
            <li className="py-3 px-5 border-t border-gray-100">
              <button
                className="text-label-sm leading-6 text-gray-800 w-full text-left"
                onClick={() => {
                  dispatch(
                    updateCommonReducerData({
                      ...commonState,
                      requestCallbackModalIsOpen: {
                        modalIsOpen: true,
                        type: FORM_TYPE_LAB_TEST,
                      },
                    })
                  )
                  onOpenChange()
                }}
              >
                Request a callback
              </button>
            </li>
            <li className="py-3 px-5 border-t border-gray-100">
              <Link
                href={NEARBY_LABS_PAGE}
                className="text-label-sm leading-6 text-gray-800"
                onClick={() => {
                  trackEvent(eventConstants.NEARBY_LABS, {
                    u_mb: loggedInMobileNumber || '',
                  })
                  onOpenChange()
                }}
              >
                Nearby Labs
              </Link>
            </li>
            {/* <li className="py-3 px-5 border-t border-gray-100">
              <Link
                href={BLOG_PAGE}
                className="text-label-sm leading-6 text-gray-800"
              >
                Blog
              </Link>
            </li> */}
          </ul>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

export default MobileDrawer
